import { GuidelineFragment } from "src/state/gql/_types"
import { FormSchema } from "src/utils/forms"

export type TalentAdminBookingGuidelineUpdateFragment = Omit<
    GuidelineFragment,
    | 'id'
    | 'role'
    | 'status'
    | 'type'
    | 'created_at'
    | 'updated_at'
    | 'created_by_user'
    | 'updated_by_user'
    | 'guideline_status'
    | 'guideline_type'
    | 'guideline_role'
    | 'document'
    | 'parent_guideline'
    | 'child_guidelines'
    | 'target_booking_status_enum'
    | 'target_booking_type_enum'
    | 'tenant_id'
    | 'venue_id'
    | 'area_id'
    | 'talent_id'
    | 'booking_id'
    | 'parent_guideline_id'
    | 'deleted'
    | 'created_by'
    | 'updated_by'
    | 'tenant_required'
    | 'venue_required'
    | 'tenant_completed'
    | 'venue_completed'
    | 'tenant_confirmed'
    | 'venue_confirmed'
>

export type TalentAdminBookingGuidelineInfoFragment = Omit<GuidelineFragment,
    | 'id'
    | 'tenant_completed'
    | 'venue_confirmed'
    | 'tenant_confirmed'
    | 'venue_completed'
    | 'talent_completed'
    | 'tenant_required'
    | 'venue_required'
    | 'talent_required'
    | 'type'
    | 'status'
    | 'created_at'
    | 'updated_at'
    | 'created_by_user'
    | 'updated_by_user'
    | 'guideline_status'
    | 'guideline_type'
    | 'guideline_role'
    | 'document'
    | 'parent_guideline'
    | 'child_guidelines'
    | 'target_booking_status_enum'
    | 'target_booking_type_enum'
    | 'tenant_id'
    | 'venue_id'
    | 'area_id'
    | 'talent_id'
    | 'booking_id'
    | 'parent_guideline_id'
    | 'deleted'
    | 'created_by'
    | 'updated_by'>

export const TalentAdminBookingGuidelineInfoSchema: FormSchema<TalentAdminBookingGuidelineInfoFragment> = {
    id: "guideline",
    title: "Guideline",
    groups: {
        default: {
            label: 'Default'
        }
    },
    fields: {
        name: {
            group: 'default',
            label: "Name",
            type: "text-display",
            grid: 6
        },
        parent_guideline_type: {
            group: 'default',
            label: "Type",
            type: "text-display",
            grid: 6
        },
        description: {
            group: 'default',
            label: "Guidelines",
            type: "text-display",
            grid: 12
        },
        information: {
            group: 'default',
            label: "Notes / Comments",
            type: "textarea",
            grid: 12,
            divider: true,
        },
        talent_confirmed: {
            group: 'default',
            label: "Talent Confirmation",
            help: "I confirm that i have reviewed and agree to the guideline above",
            type: "boolean",
            grid: 12,
            validation: {
                compare_fields: {
                    message: 'Talent Confirmation is required'
                }
            }
        },
        document_id: {
            group: 'default',
            label: "Attachment",
            type: "guideline-document-upload",
            grid: 12,
        },
    }
}

export type TalentAdminBookingGuidelineTaskFragment = Omit<GuidelineFragment, | 'id'
    | 'venue_completed'
    | 'tenant_confirmed'
    | 'venue_confirmed'
    | 'talent_confirmed'
    | 'tenant_completed'
    | 'tenant_required'
    | 'venue_required'
    | 'talent_required'
    | 'type'
    | 'status'
    | 'created_at'
    | 'updated_at'
    | 'created_by_user'
    | 'updated_by_user'
    | 'guideline_status'
    | 'guideline_type'
    | 'guideline_role'
    | 'document'
    | 'parent_guideline'
    | 'child_guidelines'
    | 'target_booking_status_enum'
    | 'target_booking_type_enum'
    | 'tenant_id'
    | 'venue_id'
    | 'area_id'
    | 'talent_id'
    | 'booking_id'
    | 'parent_guideline_id'
    | 'deleted'
    | 'created_by'
    | 'updated_by'>

export const TalentAdminBookingGuidelineTaskSchema: FormSchema<TalentAdminBookingGuidelineTaskFragment> = {
    id: "guideline",
    title: "Guideline",
    groups: {
        default: {
            label: 'Default'
        }
    },
    fields: {
        name: {
            group: 'default',
            label: "Name",
            type: "text-display",
            grid: 6
        },
        parent_guideline_type: {
            group: 'default',
            label: "Type",
            type: "text-display",
            grid: 6
        },
        description: {
            group: 'default',
            label: "Guidelines",
            type: "text-display",
            grid: 12
        },
        information: {
            group: 'default',
            label: "Notes / Comments",
            type: "textarea",
            grid: 12,
            divider: true,
        },
        talent_completed: {
            group: 'default',
            label: "Talent Completed",
            help: "I confirm that I have completed the task above",
            type: "boolean",
            grid: 12,
            validation: {
                compare_fields: {
                    message: 'Talent Completion is required'
                }
            }
        },
        document_id: {
            group: 'default',
            label: "Attachment",
            type: "guideline-document-upload",
            grid: 12,
        },
    }
}

export type TalentAdminBookingGuidelineQuestionFragment = Omit<GuidelineFragment, | 'id'
    | 'tenant_confirmed'
    | 'tenant_completed'
    | 'venue_confirmed'
    | 'talent_confirmed'
    | 'venue_completed'
    | 'tenant_required'
    | 'venue_required'
    | 'talent_required'
    | 'type'
    | 'status'
    | 'created_at'
    | 'updated_at'
    | 'created_by_user'
    | 'updated_by_user'
    | 'guideline_status'
    | 'guideline_type'
    | 'guideline_role'
    | 'document'
    | 'parent_guideline'
    | 'child_guidelines'
    | 'target_booking_status_enum'
    | 'target_booking_type_enum'
    | 'tenant_id'
    | 'venue_id'
    | 'area_id'
    | 'talent_id'
    | 'booking_id'
    | 'parent_guideline_id'
    | 'deleted'
    | 'created_by'
    | 'updated_by'>

export const TalentAdminBookingGuidelineQuestionSchema: FormSchema<TalentAdminBookingGuidelineQuestionFragment> = {
    id: "guideline",
    title: "Guideline",
    groups: {
        default: {
            label: 'Default'
        }
    },
    fields: {
        name: {
            group: 'default',
            label: "Name",
            type: "text-display",
            grid: 6
        },
        parent_guideline_type: {
            group: 'default',
            label: "Type",
            type: "text-display",
            grid: 6
        },
        description: {
            group: 'default',
            label: "Guideline",
            type: "text-display",
            grid: 12,
            divider: true,
        },
        metadata: {
            group: 'default',
            label: "Questions to Answer",
            type: "guideline-metadata-renderer",
            grid: 12,
            divider: true,
            validation: {
                validate_metadata: {
                    message: 'Some required questions are missing answers'
                }
            }
        },
        information: {
            group: 'default',
            label: "Notes / Comments",
            type: "textarea",
            grid: 12,
            divider: true,
        },
        talent_completed: {
            group: 'default',
            label: "Verify Response",
            help: "I confirm that I have answered and verified the answer above",
            type: "boolean",
            grid: 12,
            validation: {
                compare_fields: {
                    message: 'Answer Verification is required'
                }
            }
        },
        document_id: {
            group: 'default',
            label: "Attachment",
            type: "guideline-document-upload",
            grid: 12,
        },
    }
}